/**
 * The strings the **app** owns, mirroring ios/MysliKrok/Resources/Strings.swift key for key, which in turn
 * mirrors android/src/main/res/values/strings.xml.
 *
 * Slovak is the base and the fallback (BR-28). Every word about a child's answer comes from the API — see
 * frontend/design-system/12-Localisation.md §3. One language, so a catalogue would be ceremony; all three
 * clients move to catalogues together when a second language lands.
 *
 * A string with a placeholder is a function here and a `static func` on iOS. It is still one string with
 * one template — never a sentence assembled from fragments, which BR-13 forbids.
 *
 * `./check-parity.sh` compares these keys against the other two clients.
 */
export const Strings = {
  appName: 'MysliKrok',
  actionSignIn: 'Prihlásiť sa',
  actionSigningIn: 'Prihlasujem…',
  actionSignOut: 'Odhlásiť sa',
  actionRetry: 'Skúsiť znova',
  actionStartTraining: 'Začať tréning',
  actionContinue: 'Pokračovať',
  labelEmail: 'E-mail',
  labelPassword: 'Heslo',
  navChildren: 'Deti',
  navOverview: 'Prehľad',
  navSettings: 'Nastavenia',
  signInSubtitle: 'Prihláste sa a vyberte profil dieťaťa.',
  childrenEmptyTitle: 'Zatiaľ tu nie je žiadny profil',
  childrenEmptyBody: 'Pridajte dieťa a môžete začať s dnešným tréningom.',
  stateLoadFailed: 'Nepodarilo sa načítať. Skúsime to znova?',
  childSwitchBack: 'Späť k rodičovi',
  childNavHome: 'Domov',
  childSwitchBackConfirm: 'Chceš skončiť a vrátiť sa k rodičovi?',
  actionStay: 'Zostať',
  childNavIsland: 'Ostrov',
  childNavAchievements: 'Ocenenia',
  placeholderNotBuilt: 'Táto časť sa pripravuje.',
  welcomeTagline: 'Krátky denný tréning, ktorý rastie spolu s dieťaťom.',
  actionCreateAccount: 'Vytvoriť účet',
  actionBackToIntro: 'Späť na úvod',
  actionBack: 'Späť',
  sessionMemorise: 'Zapamätaj si to…',
  actionDetails: 'Podrobnosti',
  overviewAccountTitle: 'Účet',
  overviewEmailVerified: 'Adresa je potvrdená',
  overviewEmailUnverified: 'Adresa ešte nie je potvrdená',
  overviewChildrenTitle: 'Deti',
  overviewEmpty: 'Zatiaľ tu nie je žiadne dieťa. Keď pridáte prvé, uvidíte tu jeho tréning.',
  overviewTotalsTitle: 'Spolu',
  overviewTodayNone: 'Dnes zatiaľ bez tréningu',
  overviewTodayInProgress: 'Dnes rozrobené',
  overviewTodayCompleted: 'Dnes dokončené',
  overviewCalibration: 'Prvý týždeň',
  overviewDays: (arg1: number): string => `Dní tréningu: ${arg1}`,
  overviewMinutes: (arg1: number): string => `Čas: ${arg1} min`,
  overviewChildCount: (arg1: number): string => `Detí: ${arg1}`,
  actionCreatingAccount: 'Vytváram účet…',
  actionHaveAccount: 'Už mám účet',
  labelDisplayName: 'Meno (nepovinné)',
  registerSubtitle: 'Stačí e-mail a heslo. Profil dieťaťa pridáte hneď potom.',
  verifyEmailBanner: 'Potvrďte svoj e-mail. Poslali sme vám odkaz.',
  verifyEmailResend: 'Poslať znova',
  verifyEmailSent: 'Odkaz je na ceste. Pozrite si schránku.',
  verifyEmailFixAddress: 'Opraviť adresu',
  verifyEmailConfirming: 'Potvrdzujeme vašu adresu…',
  verifyEmailDone: 'Adresa je potvrdená. Ďakujeme!',
  verifyEmailUnverified: 'Nepotvrdené',
  verificationLinkMissing: 'Tento odkaz je neúplný. Otvorte ho prosím priamo z e-mailu.',
  registerDone: 'Účet je pripravený. Prihláste sa heslom, ktoré ste si zvolili.',
  labelPasswordConfirmation: 'Heslo ešte raz',
  registerPasswordHint: 'Aspoň 10 znakov.',
  registerPasswordMismatch: 'Heslá sa nezhodujú. Skontrolujte ich prosím.',
  actionForgotPassword: 'Zabudnuté heslo',
  forgotPasswordTitle: 'Obnovenie hesla',
  forgotPasswordBody: 'Zadajte e-mail a pošleme naň odkaz na nastavenie nového hesla.',
  forgotPasswordSent: 'Ak k tejto adrese patrí účet, poslali sme naň odkaz. Skontrolujte si e-mail.',
  actionSend: 'Odoslať',
  oauthUnavailable: 'Prihlásenie cez Google, Facebook a Apple sa pripravuje.',
  actionAddChild: 'Pridať dieťa',
  actionSave: 'Uložiť',
  actionSaving: 'Ukladám…',
  labelNickname: 'Ako dieťa oslovujeme',
  labelBirthYear: 'Rok narodenia',
  labelSchoolGrade: 'Ročník',
  childSummary: (arg1: number, arg2: number): string => `${arg1} · ${arg2}. ročník`,
  childInCalibration: 'Objavujeme superschopnosti',
  overviewPickChild: 'Vyberte dieťa',
  overviewTrainingDays: 'Dní tréningu',
  overviewTotalTime: 'Celkový čas',
  overviewAttempts: 'Odpovedí',
  overviewSkills: 'Oblasti',
  overviewRecommendations: 'Čo odporúčame',
  overviewErrorPatterns: 'Opakované chyby',
  overviewNoPatterns: 'Zatiaľ sa neopakuje žiadna konkrétna chyba.',
  overviewCalibrationNote: 'Prvý týždeň ešte spoznávame dieťa, úrovne sa môžu meniť.',
  skillDetailTitle: 'Podrobnosti',
  skillLevel: (arg1: number, arg2: number): string => `Úroveň ${arg1} z ${arg2}`,
  skillAccuracy: (arg1: number): string => `Úspešnosť ${arg1} %`,
  skillNoData: 'zatiaľ bez údajov',
  settingsAccount: 'Účet',
  settingsLanguage: 'Jazyk',
  settingsLanguageValue: 'Slovenčina',
  settingsSubscription: 'Predplatné',
  settingsSubscriptionNote: 'Zatiaľ je všetko zadarmo, nič sa neúčtuje.',
  childHomeGreeting: (arg1: string): string => `Ahoj, ${arg1}!`,
  childHomeContinue: 'Pokračovať v tréningu',
  childHomeUnfinished: (arg1: string, arg2: string): string => `Máš rozrobené ${arg1} z ${arg2} úloh`,
  childHomeDoneToday: 'Dnešný tréning máš hotový. Uvidíme sa zajtra!',
  childHomeToday: 'Dnešný tréning',
  childHomeIsland: 'Môj ostrov',
  childStars: 'Hviezdy',
  childCoins: 'Mince',
  sessionAnswerLabel: 'Tvoja odpoveď',
  sessionOrderingHint: 'Klepni na slová v poradí.',
  sessionHint: 'Nápoveda',
  sessionCheck: 'Skontrolovať',
  sessionNext: 'Ďalšia úloha',
  sessionFinish: 'Dokončiť',
  sessionProgress: (arg1: number, arg2: number): string => `Úloha ${arg1} z ${arg2}`,
  sessionUnsupported: 'Túto úlohu zatiaľ nevieme zobraziť. Preskočíme ju.',
  islandUnlock: 'Odomknúť',
  islandStillNeed: (arg1: string): string => `Ešte ${arg1} mincí`,
  islandEarnPerSession: (arg1: string): string => `Za dokončený tréning získaš aspoň ${arg1} mincí`,
  islandLocked: 'Zatiaľ zamknuté',
  islandUnlocked: 'Odomknuté',
  resetPasswordTitle: 'Nastavenie nového hesla',
  resetPasswordBody: 'Zvoľte si nové heslo. Odkaz sa dá použiť len raz.',
  labelNewPassword: 'Nové heslo',
  actionSetPassword: 'Nastaviť heslo',
  resetPasswordDone: 'Heslo je zmenené. Teraz sa prihláste novým heslom.',
  resetPasswordInvalid: 'Tento odkaz už neplatí alebo bol použitý. Požiadajte o nový.',
  childHomeAchievements: 'Moje ocenenia',
  achievementsEarned: 'Získané',
  achievementsBestStreak: 'Najviac dní po sebe',
  achievementsNextUp: 'Najbližšie méty',
  achievementsEmpty: 'Tvoje prvé ocenenie príde po prvom dokončenom tréningu.',
  achievementsNew: 'Nové ocenenie',
  overviewAchievements: 'Ocenenia',

  // The reveal control in a password field. Two labels rather than one, because the button's name has to
  // say what pressing it does, and that changes with the state (R0144).
  signInWithGoogle: 'Pokračovať cez Google',
  signInWithFacebook: 'Pokračovať cez Facebook',
  signInWithProvider: 'alebo',
  signInProviderFailed: 'Prihlásenie cez poskytovateľa sa nepodarilo. Skúste to znova alebo použite heslo.',

  passwordShow: 'Zobraziť heslo',
  passwordHide: 'Skryť heslo',
} as const;
